import React, { useEffect, useState, useCallback } from "react";
import { Search, Plus, Download, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UYGUNLUK } from "@/lib/utils-tr";
import { toast } from "sonner";

export default function AdminProducts() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [durum, setDurum] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const qs = new URLSearchParams({ q, page, page_size: 30 });
    if (durum !== "all") qs.set("yayin_durumu", durum);
    api.get(`/admin/products?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [q, durum, page]);

  useEffect(() => { load(); }, [load]);

  // Satır içi hızlı yayın durumu değiştirme (düzenleme penceresi gerekmez)
  const setDurumInline = async (p, durum) => {
    if (p.yayin_durumu === durum) return;
    try {
      await api.patch(`/admin/products/${p.urun_id}`, { yayin_durumu: durum });
      setData((d) => ({
        ...d,
        items: d.items.map((x) => (x.urun_id === p.urun_id ? { ...x, yayin_durumu: durum } : x)),
      }));
      toast.success(`"${p.urun_adi_tr}" → ${durum}`);
    } catch (e) {
      toast.error("Durum güncellenemedi");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="font-head text-2xl font-bold text-slate-900">Ürün Yönetimi</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/admin/products-export?format=xlsx`, "_blank")} data-testid="export-xlsx-btn"><Download className="w-4 h-4 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/admin/products-export?format=csv`, "_blank")} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-btn"><Upload className="w-4 h-4 mr-1" />İçe aktar</Button>
          <Button size="sm" onClick={() => navigate("/yonetim/urunler/yeni")} className="bg-teal-600 hover:bg-teal-700" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-1" />Yeni Ürün Ekle</Button>
        </div>
      </div>
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Ürün ara (ad, kod, CAS, marka)..."
            className="pl-9" data-testid="admin-product-search" />
        </div>
        <Select value={durum} onValueChange={(v) => { setDurum(v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="admin-product-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="Aktif">Aktif (Yayında)</SelectItem>
            <SelectItem value="taslak">Taslak</SelectItem>
            <SelectItem value="pasif">Pasif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="excel-table w-full text-sm" data-testid="admin-products-table">
          <thead><tr>
            <th className="text-left px-3 py-2.5">Kod</th><th className="text-left px-3 py-2.5">Ürün</th>
            <th className="text-left px-3 py-2.5">Marka</th><th className="text-left px-3 py-2.5">Durum</th>
            <th className="text-left px-3 py-2.5">Uygunluk</th><th className="px-3 py-2.5"></th>
          </tr></thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.urun_id} data-testid={`admin-row-${p.urun_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.urun_kodu}</td>
                <td className="px-3 py-2">{p.urun_adi_tr}</td>
                <td className="px-3 py-2">{p.marka}</td>
                <td className="px-3 py-2">
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid={`durum-toggle-${p.urun_id}`}>
                    {["Aktif", "taslak", "pasif"].map((d) => (
                      <button
                        key={d}
                        data-testid={`durum-${d}-${p.urun_id}`}
                        onClick={() => setDurumInline(p, d)}
                        title={d === "Aktif" ? "Yayına al" : d === "taslak" ? "Taslağa çek" : "Pasifleştir"}
                        className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                          p.yayin_durumu === d
                            ? d === "Aktif"
                              ? "bg-emerald-600 text-white"
                              : d === "pasif"
                                ? "bg-red-600 text-white"
                                : "bg-slate-500 text-white"
                            : "bg-white text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        {d === "Aktif" ? "Aktif" : d === "taslak" ? "Taslak" : "Pasif"}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs">{UYGUNLUK[p.satis_uygunlugu]?.label || "Bireysel"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`quick-edit-${p.urun_id}`} className="mr-1">Hızlı</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/yonetim/urunler/${p.urun_id}`)} data-testid={`edit-${p.urun_id}`}>Düzenle</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-sm text-slate-500">{data.total} ürün · Sayfa {page}/{data.pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Önceki</Button>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Sonraki</Button>
        </div>
      </div>

      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {editing && <EditDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (uygula) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/admin/products-import?uygula=${uygula}`, fd);
      if (uygula) { toast.success(`${r.data.yeni} yeni, ${r.data.guncellenen} güncellenen ürün`); onDone(); } else setPrev(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="import-dialog">
        <DialogHeader><DialogTitle>Excel / CSV ile içe aktar</DialogTitle>
          <DialogDescription>Dışa aktarılan dosyanın sütun başlıklarını kullanın. urun_id dolu satırlar mevcut ürünü günceller, boş olanlar taslak olarak yeni ürün oluşturur. Hiçbir ürün silinmez; değişen alanların önceki hali yedeklenir.</DialogDescription></DialogHeader>
        <input type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files[0]); setPrev(null); }} data-testid="import-file" />
        {prev && <div className="text-sm bg-slate-50 border rounded p-3" data-testid="import-preview"><p>{prev.satir} satır · <b>{prev.yeni}</b> yeni · <b>{prev.guncellenecek}</b> güncellenecek · {prev.hata_sayisi} hata</p>
          {prev.hatalar.length > 0 && <ul className="text-xs text-red-600 mt-1 max-h-32 overflow-y-auto">{prev.hatalar.map((h) => <li key={h}>{h}</li>)}</ul>}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          {!prev ? <Button disabled={!file || busy} onClick={() => send(false)} data-testid="import-preview-btn">Önizle</Button>
            : <Button disabled={busy || (!prev.yeni && !prev.guncellenecek)} className="bg-teal-600 hover:bg-teal-700" onClick={() => window.confirm(`${prev.yeni} yeni ürün eklenecek, ${prev.guncellenecek} ürün güncellenecek. Onaylıyor musunuz?`) && send(true)} data-testid="import-apply-btn">Onayla ve uygula</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ product, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({
    yayin_durumu: product.yayin_durumu, satis_uygunlugu: product.satis_uygunlugu || "bireysel",
    kisa_aciklama: product.kisa_aciklama || "", gerekli_belge_notu: product.gerekli_belge_notu || "",
    seo_title: product.seo_title || "", seo_description: product.seo_description || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/admin/products/${product.urun_id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [product.urun_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/products/${product.urun_id}`, f);
      toast.success("Ürün güncellendi");
      onSaved();
    } catch (e) { toast.error("Kaydedilemedi"); } finally { setSaving(false); }
  };

  const genSeo = async () => {
    try {
      await api.post("/admin/seo/generate", { urun_ids: [product.urun_id] });
      const r = await api.get(`/admin/products/${product.urun_id}`);
      set("seo_title", r.data.product.seo_title || "");
      set("seo_description", r.data.product.seo_description || "");
      toast.success("SEO metni üretildi");
    } catch (e) { toast.error("SEO üretilemedi"); }
  };

  const price = detail?.price;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-edit-dialog">
        <DialogHeader><DialogTitle className="font-head">{product.urun_adi_tr}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{product.urun_kodu} · {product.marka}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Yayın Durumu</Label>
              <Select value={f.yayin_durumu} onValueChange={(v) => set("yayin_durumu", v)}>
                <SelectTrigger className="mt-1.5" data-testid="edit-yayin-durumu"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aktif">Aktif (Yayında)</SelectItem>
                  <SelectItem value="taslak">Taslak</SelectItem>
                  <SelectItem value="pasif">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Satış Uygunluğu</Label>
              <Select value={f.satis_uygunlugu} onValueChange={(v) => set("satis_uygunlugu", v)}>
                <SelectTrigger className="mt-1.5" data-testid="edit-satis-uygunlugu"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bireysel">Bireysel Talebe Açık</SelectItem>
                  <SelectItem value="kurum">Yalnızca Firma/Kurum</SelectItem>
                  <SelectItem value="inceleme">İnceleme Gerekli</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Kısa Açıklama</Label>
            <Textarea value={f.kisa_aciklama} onChange={(e) => set("kisa_aciklama", e.target.value)} className="mt-1.5" data-testid="edit-kisa-aciklama" />
          </div>
          <div>
            <Label>Gerekli Belge / Kullanım Amacı Notu</Label>
            <Input value={f.gerekli_belge_notu} onChange={(e) => set("gerekli_belge_notu", e.target.value)} className="mt-1.5" data-testid="edit-belge-notu" />
          </div>

          {price && (
            <div className="bg-slate-50 border rounded-lg p-3 text-sm">
              <div className="font-medium text-slate-700 mb-1">Maliyet Bilgisi (yalnızca yönetim)</div>
              <div className="text-slate-600">
                Ambalaj maliyeti: <b>{price.ambalaj_adet_fiyati ?? "—"} {price.para_birimi || ""}</b> ·
                KDV: %{price.kdv_orani != null ? price.kdv_orani * 100 : "—"}
                {price.fiyat_notu && <span> · Not: {price.fiyat_notu}</span>}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-head font-semibold">SEO</Label>
              <Button size="sm" variant="outline" onClick={genSeo} data-testid="gen-seo-btn">Yapay Zekâ ile Üret</Button>
            </div>
            <Input value={f.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="SEO Başlığı" className="mb-2" data-testid="edit-seo-title" />
            <Textarea value={f.seo_description} onChange={(e) => set("seo_description", e.target.value)} placeholder="Meta Açıklama" data-testid="edit-seo-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-product-btn">
            {saving ? "..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
